import { Button } from '@creditornot/cb-button';
import { Checkbox } from '@creditornot/cb-checkbox';
import { CaretLeft, CaretRight } from '@creditornot/cb-icons';
import { colors, cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { DateTime, Interval, Info, MonthNumbers } from 'luxon';
import R from 'ramda';
import React, { useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { DatePeriod } from './types';

type Props = {
  timezone: string;
  value: DatePeriod[];
  onChange: (periods: DatePeriod[]) => void;
  onDateModeChange?: (pickerMode: 'single' | 'period') => void;
};

type PartialDatePeriod = {
  start: DateTime | null;
  end: DateTime | null;
};

type MonthViewDayData = {
  value: DateTime;
  inPrevMonth: boolean;
  inNextMonth: boolean;
  weekend: boolean;
};

type MonthViewDayState = {
  selectable: boolean;
  selected: boolean;
  inRange: boolean;
};

type MonthViewDayActions = {
  select(): void;
  hover(): void;
};

type MonthViewDay = MonthViewDayData & MonthViewDayState & MonthViewDayActions;

type MonthViewMatrix = MonthViewDay[][];

type WeekdayIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const SpecialHoursDatePicker = ({ timezone, value, onChange, onDateModeChange }: Props) => {
  const [now] = useState(DateTime.fromMillis(Date.now(), { zone: timezone }));

  const [yearToView, setYearToView] = useState(now.year);
  const [monthToView, setMonthToView] = useState(now.month);
  const [hoveredValue, setHoveredValue] = useState<DateTime | null>(null);
  const [pickerMode, setPickerMode] = useState<'single' | 'period'>(
    value.every(period => Interval.fromDateTimes(period.start, period.end).length('days') === 0) ? 'single' : 'period',
  );

  const [dates, setDates] = useState(pickerMode === 'single' ? value.map(p => p.start) : []);
  const [period, setPeriod] = useState<PartialDatePeriod>(
    pickerMode === 'period' ? value[0] : { start: null, end: null },
  );

  useEffect(() => {
    const pMode = value.every(p => Interval.fromDateTimes(p.start, p.end).length('days') === 0) ? 'single' : 'period';
    setPickerMode(pMode);
    setDates(pMode === 'single' ? value.map(p => p.start) : []);
    setPeriod(pMode === 'period' ? value[0] : { start: null, end: null });
  }, [value]);

  const handlePrevMonth = () => {
    if (monthToView === 1) {
      setYearToView(yearToView - 1);
      setMonthToView(12);
    } else {
      setMonthToView((monthToView - 1) as MonthNumbers);
    }
  };

  const handleNextMonth = () => {
    if (monthToView === 12) {
      setYearToView(yearToView + 1);
      setMonthToView(1);
    } else {
      setMonthToView((monthToView + 1) as MonthNumbers);
    }
  };

  const handleSelect = (selectedValue: DateTime) => {
    if (pickerMode === 'single') {
      if (R.contains(selectedValue, dates)) {
        setDates(R.remove(R.indexOf(selectedValue, dates), 1, dates));
      } else {
        setDates([...dates, selectedValue]);
      }
    } else if (!period.start) {
      setPeriod({
        ...period,
        start: selectedValue,
      });
    } else if (!period.end) {
      setPeriod({
        start: selectedValue >= period.start ? period.start : selectedValue,
        end: selectedValue > period.start ? selectedValue : period.start,
      });
    } else {
      setPeriod({
        start: selectedValue,
        end: null,
      });
    }
  };

  const handleTogglePeriod = () => {
    const newMode = pickerMode === 'single' ? 'period' : 'single';
    setPickerMode(newMode);
    if (onDateModeChange) {
      onDateModeChange(newMode);
    }
  };

  const handleHover = (date: DateTime) => {
    if (pickerMode === 'period') {
      setHoveredValue(date);
    }
  };

  const isDateSelectable = useCallback(
    (date: DateTime) => {
      return date >= now.startOf('day');
    },
    [now],
  );

  const handleDone = () => {
    if (pickerMode === 'single') {
      onChange(dates.map(date => ({ start: date, end: date })));
    } else if (period.start && period.end) {
      onChange([{ start: period.start, end: period.end }]);
    }
  };

  return (
    <DatePicker
      hoveredValue={hoveredValue}
      isDateSelectable={isDateSelectable}
      mode={pickerMode}
      monthToView={monthToView}
      nextMonth={handleNextMonth}
      onDone={handleDone}
      onHover={handleHover}
      onSelect={handleSelect}
      onTogglePeriod={handleTogglePeriod}
      prevMonth={handlePrevMonth}
      selectedDates={dates}
      selectedPeriod={period}
      timezone={timezone}
      yearToView={yearToView}
    />
  );
};

type DatePickerProps = {
  hoveredValue: DateTime | null;
  mode: 'single' | 'period';
  monthToView: number;
  selectedDates: DateTime[];
  selectedPeriod: PartialDatePeriod;
  timezone: string;
  yearToView: number;
  isDateSelectable: (date: DateTime) => boolean;
  nextMonth: () => void;
  prevMonth: () => void;
  onDone: () => void;
  onHover: (date: DateTime) => void;
  onSelect: (value: DateTime) => void;
  onTogglePeriod: () => void;
};

function* monthViewDays(
  year: number,
  month: number,
  firstDayOfWeek: WeekdayIndex,
  timezone?: string,
): Generator<MonthViewDayData, void, void> {
  const firstDayOfMonth = DateTime.fromObject(
    {
      year,
      month,
      day: 1,
    },
    {
      zone: timezone,
    },
  );

  const firstDayOfFirstWeek = firstDayOfMonth.minus({ days: (firstDayOfMonth.weekday - firstDayOfWeek + 7) % 7 });
  const dayAfterLastDay = firstDayOfFirstWeek.plus({ days: 6 * 7 });

  let currentDay = firstDayOfFirstWeek;
  while (currentDay < dayAfterLastDay) {
    yield {
      value: currentDay,
      inPrevMonth: currentDay.month < month || currentDay.year < year,
      inNextMonth: currentDay.month > month || currentDay.year > year,
      weekend: currentDay.weekday === 6 || currentDay.weekday === 7,
    };
    currentDay = currentDay.plus({ days: 1 });
  }
}

const buildMonthViewMatrix = (
  mode: 'single' | 'period',
  firstDayOfWeek: WeekdayIndex,
  yearToView: number,
  monthToView: number,
  hoveredValue: DateTime | null,
  selectedDates: DateTime[],
  selectedPeriod: PartialDatePeriod,
  timezone: string,
  onSelect: (value: DateTime) => void,
  onHover: (date: DateTime) => void,
  isDateSelectable: (date: DateTime) => boolean,
): MonthViewMatrix => {
  const isSelected = (dayData: MonthViewDayData) => {
    if (mode === 'single') {
      if (!selectedDates.length) {
        return false;
      }

      return R.contains(dayData.value, selectedDates);
    }

    return (
      R.equals(selectedPeriod.start?.valueOf(), dayData.value.valueOf()) ||
      R.equals(selectedPeriod.end?.valueOf(), dayData.value.valueOf())
    );
  };

  const isInRange = (dayData: MonthViewDayData) => {
    if (mode === 'period') {
      if (selectedPeriod.start && selectedPeriod.end) {
        return Interval.fromDateTimes(selectedPeriod.start, selectedPeriod.end).contains(dayData.value);
      }

      if (selectedPeriod.start && hoveredValue) {
        return (
          selectedPeriod.start <= hoveredValue
            ? Interval.fromDateTimes(selectedPeriod.start, hoveredValue)
            : Interval.fromDateTimes(hoveredValue, selectedPeriod.start)
        ).contains(dayData.value);
      }

      return false;
    }

    return false;
  };

  const days: MonthViewDay[] = [...monthViewDays(yearToView, monthToView, firstDayOfWeek, timezone)].map(dayData => {
    const selectable = isDateSelectable(dayData.value);
    return {
      ...dayData,
      selectable,
      inRange: mode === 'period' ? isInRange(dayData) : false,
      selected: isSelected(dayData),
      select: () => {
        if (selectable) {
          onSelect(dayData.value);
        }
      },
      hover: () => {
        onHover(dayData.value);
      },
    };
  });

  return R.splitEvery(7, days);
};

const Container = styled.div`
  padding: 24px 32px 32px;
  user-select: none;
  ${typographyCss.Body3}
`;

const YearMonthControl = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-block-end: 24px;
  ${typographyCss.Body2}
`;

const MonthArrow = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  margin: -16px;
  cursor: pointer;
`;

const YearMonthLabel = styled.div`
  text-align: center;
`;

const Calendar = styled.div``;

const CalendarRow = styled.div`
  display: flex;
  justify-content: center;
`;

const WeekdayRow = styled(CalendarRow)`
  color: ${cssThemedValue('textTertiary')};
`;

const CalendarCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 40px;
  width: 14%;
`;

const CalendarCellInner = styled.div`
  width: 100%;
  height: 32px;
  line-height: 32px;

  text-align: center;
  overflow: hidden;
  white-space: nowrap;
`;

const CalendarDayCell = styled(({ monthViewDay, ...rest }: React.PropsWithChildren<{ monthViewDay: MonthViewDay }>) => (
  <CalendarCell onClick={monthViewDay.select} onMouseOver={monthViewDay.hover} {...rest} />
))`
  ${CalendarCellInner} {
    background-color: ${({ monthViewDay }) => {
      if (monthViewDay.selected) {
        return colors.wolt100;
      }
      if (monthViewDay.inRange) {
        return cssThemedValue({ light: colors.wolt8, dark: colors.salt12 });
      }
      return 'unset';
    }};
  }

  &:hover ${CalendarCellInner} {
    background-color: ${colors.wolt100};
    color: ${cssThemedValue('surfaceMain')};
  }

  color: ${({ monthViewDay }) => {
    if (monthViewDay.selected) {
      return cssThemedValue('surfaceMain');
    }

    if (!monthViewDay.selectable) {
      return cssThemedValue({ light: colors.pepper16, dark: colors.salt20 });
    }

    if (monthViewDay.inPrevMonth || monthViewDay.inNextMonth) {
      return cssThemedValue('textTertiary');
    }

    return 'inherit';
  }};

  cursor: ${({ monthViewDay }) => (monthViewDay.selectable ? 'pointer' : 'not-allowed')};
`;

const PrevIcon = styled(CaretLeft)`
  width: 14px;
  height: 14px;
  fill: ${cssThemedValue('textPrimary')};
`;

const NextIcon = styled(CaretRight)`
  width: 14px;
  height: 14px;
  fill: ${cssThemedValue('textPrimary')};
`;
const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-block-start: 24px;
`;

const StyledCheckbox = styled(Checkbox)`
  &:not(:last-of-type) {
    margin-inline-end: 16px;
  }
`;

const DatePicker = ({
  hoveredValue,
  selectedDates,
  selectedPeriod,
  onHover,
  onSelect,
  onTogglePeriod,
  onDone,
  isDateSelectable,
  yearToView,
  monthToView,
  prevMonth,
  nextMonth,
  timezone,
  mode,
}: DatePickerProps) => {
  const formatYearAndMonth = (year: number, month: number) =>
    DateTime.fromObject({
      year,
      month,
    }).toFormat('LLLL yyyy');
  const weekdayLabels = Info.weekdays('narrow');
  const firstDayOfWeek = 1 as const;

  const monthViewMatrix = buildMonthViewMatrix(
    mode,
    firstDayOfWeek,
    yearToView,
    monthToView,
    hoveredValue,
    selectedDates,
    selectedPeriod,
    timezone,
    onSelect,
    onHover,
    isDateSelectable,
  );

  return (
    <Container>
      <YearMonthControl>
        <MonthArrow onClick={prevMonth}>
          <PrevIcon />
        </MonthArrow>
        <YearMonthLabel>{formatYearAndMonth(yearToView, monthToView)}</YearMonthLabel>
        <MonthArrow onClick={nextMonth}>
          <NextIcon />
        </MonthArrow>
      </YearMonthControl>
      <Calendar>
        <WeekdayRow>
          {weekdayLabels.map((label, index) => (
            <CalendarCell key={index}>
              <CalendarCellInner>{label}</CalendarCellInner>
            </CalendarCell>
          ))}
        </WeekdayRow>
        {monthViewMatrix.map((monthViewWeek, weekIndex) => (
          <CalendarRow key={weekIndex}>
            {monthViewWeek.map((monthViewDay, dayIndex) => (
              <CalendarDayCell key={dayIndex} monthViewDay={monthViewDay}>
                <CalendarCellInner>{monthViewDay.value.day.toString()}</CalendarCellInner>
              </CalendarDayCell>
            ))}
          </CalendarRow>
        ))}
      </Calendar>
      <Controls>
        <StyledCheckbox onChange={onTogglePeriod} checked={mode === 'period'}>
          <FormattedMessage id="special-hours.period" defaultMessage="Period" />
        </StyledCheckbox>
        <Button onClick={onDone} variant="lightBlue" size="small">
          <FormattedMessage id="special-hours.done" defaultMessage="Done" />
        </Button>
      </Controls>
    </Container>
  );
};

export default SpecialHoursDatePicker;
